import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import PictureInPictureIcon from "@mui/icons-material/PictureInPicture";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import { colord } from "colord";
import { format } from "date-fns";

import { useAppSelector } from "app/hooks";
import { AssignedTaskTags } from "components/assigned-tags";
import type { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import { STAFF_TASK_COLORS_FEATURE } from "constants/feature-flags";
import {
  TASK_STATUS_CANCELLED,
  TASK_STATUS_COMPLETED
} from "constants/staff-tasks";
import { selectCurrentUser } from "features/auth/auth-slice";
import PriorityLabel from "features/staff-tasks-v2/components/priority-label.component";
import CommentsLink from "features/staff-tasks-v2/drawer/staff-task-card/comments-link-component";
import DueDateDisplay from "features/staff-tasks-v2/drawer/staff-task-card/due-date-display.component";
import StaffTaskAssigneeDisplay from "features/staff-tasks-v2/drawer/staff-task-card/staff-task-assignee-display.component";
import StaffTaskPatientDisplay from "features/staff-tasks-v2/drawer/staff-task-card/staff-task-patient-display.component";
import TaskStatusField from "features/staff-tasks-v2/drawer/staff-task-card/task-status-field-component";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import type { StaffTask } from "services/types/staff-tasks.type";
import { formatBackendDateOnly, getHtmlAsTextPreview } from "utils/common";

type StaffTaskRowProps = {
  task: StaffTask;
  showPatientColumn?: boolean;
  isSelected?: boolean;
  onSelectChange?: (checked: boolean) => void;
  showCheckbox?: boolean;
  checkboxDisabled?: boolean;
};

const TASK_DESCRIPTION_MAX_LENGTH = 255;

export default function StaffTaskRow({
  task,
  showPatientColumn,
  isSelected = false,
  onSelectChange,
  showCheckbox = false,
  checkboxDisabled = false
}: StaffTaskRowProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const flags = useFlags();
  const { handleStatusUpdate, handleOpenTaskModal, handleDelete } =
    useStaffTasksV2();

  const isComplete =
    task.status === TASK_STATUS_COMPLETED ||
    task.status === TASK_STATUS_CANCELLED;

  const isCancelled = task.status === TASK_STATUS_CANCELLED;

  const cellSx = {
    pl: 1.5,
    whiteSpace: "normal",
    background:
      flags[STAFF_TASK_COLORS_FEATURE] && task.color && !isComplete
        ? colord(task.color).alpha(0.15).toHex()
        : "white",
    boxSizing: "border-box",
    alignContent: "flex-start"
  };

  const hasCurrentUserInAdditionalAssignees = task.assignees?.find(
    (assignee) => assignee.user.id === currentUser?.id
  );

  const assignees = useMemo(() => {
    return !task.assignees?.length
      ? [task.assignee]
      : [
          task.assignee,
          ...task.assignees
            .filter((assignee) => assignee.user.id !== currentUser.id)
            .map((assignee) => assignee.user)
        ];
  }, [currentUser?.id, task.assignee, task.assignees]);

  const dueDateDisplay = task?.due_at
    ? formatBackendDateOnly(task.due_at, "MMM dd, yyyy")
    : null;

  const isAssigned = Boolean(
    task.assignee?.id === currentUser?.id || hasCurrentUserInAdditionalAssignees
  );

  const previewDescriptionValue = useMemo(
    () =>
      getHtmlAsTextPreview(task.description, "", TASK_DESCRIPTION_MAX_LENGTH),
    [task.description]
  );

  const openModal = useCallback(() => {
    handleOpenTaskModal({ mode: "view", taskId: task.id });
  }, [handleOpenTaskModal, task.id]);

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      if (showCheckbox && onSelectChange && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        e.stopPropagation();
        onSelectChange(!isSelected);
        return;
      }
      if (!showCheckbox || (!e.metaKey && !e.ctrlKey)) {
        openModal();
      }
    },
    [showCheckbox, onSelectChange, isSelected, openModal]
  );

  return (
    <TableRow
      sx={{
        fontSize: "50%",
        py: 1.5,
        background: isComplete ? grey[50] : isSelected ? grey[100] : undefined,
        cursor: showCheckbox ? "pointer" : undefined,
        "&:hover": {
          background: isSelected ? grey[200] : undefined
        }
      }}
      onClick={handleRowClick}
    >
      {showCheckbox && (
        <TableCell
          sx={{ ...cellSx, width: "50px", pl: 0.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onChange={(_, checked) => onSelectChange?.(checked)}
            onClick={(e) => e.stopPropagation()}
            disabled={checkboxDisabled}
          />
        </TableCell>
      )}
      <TableCell sx={{ ...cellSx, width: "86px", pl: 0 }}>
        <PriorityLabel
          priority={Number(task.priority)}
          isRow={false}
          isComplete={isComplete}
          sx={{
            fontSize: "14px",
            lineHeight: 1.2,
            color: isComplete ? grey[600] : undefined
          }}
        />
      </TableCell>
      <TableCell
        sx={{
          ...cellSx,
          pl: 1.5,
          width: "*",
          // A long unbreakable token (a pasted URL, a reference number) would otherwise set this
          // column's minimum width and squeeze Due Date / Created / Updated out of view (ENG-2111).
          // "anywhere" also lets the column collapse below its longest word, so the Stack below
          // carries a floor, or ordinary titles break mid-word.
          overflowWrap: "anywhere"
        }}
      >
        <Stack
          rowGap={1}
          onClick={(e) => {
            if (!showCheckbox) {
              openModal();
            } else {
              e.stopPropagation();
            }
          }}
          sx={{
            cursor: showCheckbox ? "default" : "pointer",
            minWidth: "120px"
          }}
        >
          <Stack direction="row" alignItems="center" columnGap={1}>
            <Typography
              variant="body3"
              sx={{
                color: isComplete ? grey[600] : undefined,
                lineHeight: 1.2,
                textDecoration: isCancelled ? "line-through" : undefined,
                "&:hover": {
                  textDecoration: "underline"
                }
              }}
            >
              {task.title}
            </Typography>
            {task.implementation != null && (
              <Chip
                label="Onboarding"
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontSize: "12px", height: "20px" }}
              />
            )}
            {task.is_training && (
              <Chip
                label="Training"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: "12px", height: "20px" }}
              />
            )}
          </Stack>
          <Typography
            variant="body1"
            sx={{
              color: !isComplete ? undefined : grey[600],
              "&:hover": {
                textDecoration: "underline"
              }
            }}
          >
            {previewDescriptionValue}
          </Typography>
          <CommentsLink
            taskId={task.id}
            count={task.comment_count ?? task.comments?.length ?? 0}
            isComplete={isComplete}
            onClick={openModal}
            sx={{ mt: 0 }}
          />
        </Stack>
      </TableCell>

      {showPatientColumn && (
        <TableCell sx={{ ...cellSx, width: "13%" }}>
          {task.patient && (
            <StaffTaskPatientDisplay
              patient={task.patient}
              isComplete={isComplete}
              isOpenInNewTab
              variant="body1"
            />
          )}
        </TableCell>
      )}

      <TableCell
        sx={{
          ...cellSx,
          width: showPatientColumn ? "13%" : "15%"
        }}
      >
        <Stack rowGap={1}>
          <StaffTaskAssigneeDisplay
            textSx={{
              fontSize: "0.875rem",
              lineHeight: "1.0625rem",
              color: isAssigned && !isComplete ? grey[900] : grey[700]
            }}
            assignees={assignees}
          />
          {hasCurrentUserInAdditionalAssignees && (
            <Typography
              variant="body1"
              sx={{
                color: isComplete ? grey[700] : undefined,
                fontWeight: !isComplete ? 700 : undefined
              }}
            >
              {currentUser?.fullName}
            </Typography>
          )}
        </Stack>
      </TableCell>

      <TableCell
        sx={{ ...cellSx, width: "10%" }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <AssignedTaskTags task={task} />
      </TableCell>

      <TableCell sx={{ ...cellSx, width: "15%" }}>
        <TaskStatusField
          value={task.status}
          completedAtDate={
            task.status === TASK_STATUS_COMPLETED
              ? task.completed_at
              : undefined
          }
          onChange={(code: StatusTransitionCode) =>
            handleStatusUpdate(Number(task?.id), code)
          }
        />
      </TableCell>

      <TableCell
        sx={{
          ...cellSx,
          width: showPatientColumn ? "13%" : "15%"
        }}
      >
        <Stack rowGap={1.5}>
          <DueDateDisplay
            date={task.due_at}
            isComplete={isComplete}
            showDate={isComplete}
          />
          <Typography
            component="div"
            variant="body1"
            sx={{
              color: grey[900],
              fontSize: "12px",
              mt: -1,
              display: "flex",
              alignSelf: "flex-start",
              pl: 2
            }}
          >
            {!isComplete && dueDateDisplay ? dueDateDisplay : null}
          </Typography>
        </Stack>
      </TableCell>

      <TableCell sx={{ ...cellSx, width: "12%" }}>
        <Tooltip
          title={
            task.created_at
              ? `at ${format(
                  new Date(task.created_at),
                  "MMM dd, yyyy hh:mm aaa"
                )}`
              : ""
          }
        >
          <Stack>
            <Typography variant="body1" sx={{ color: grey[600] }}>
              by {task?.created_by?.fullName}
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: grey[600], fontSize: "12px" }}
            >
              {task.created_at
                ? `at ${format(new Date(task.created_at), "MMM dd, yyyy")}`
                : ""}
            </Typography>
          </Stack>
        </Tooltip>
      </TableCell>

      <TableCell sx={{ ...cellSx, width: "115px" }}>
        <Tooltip
          title={
            task.updated_at
              ? `${format(new Date(task.updated_at), "MMM dd, yyyy hh:mm aaa")}`
              : ""
          }
        >
          <Stack>
            <Typography
              variant="body1"
              sx={{ color: grey[600], fontSize: "12px" }}
            >
              {task.updated_at
                ? `${format(new Date(task.updated_at), "MMM dd, yyyy")}`
                : ""}
            </Typography>
          </Stack>
        </Tooltip>
      </TableCell>

      <TableCell sx={{ ...cellSx, width: "45px" }}>
        <Stack
          sx={{
            width: "40px",
            opacity: isComplete ? 0.8 : 1,
            marginTop: "-13px"
          }}
          rowGap={0.5}
        >
          <IconButton
            sx={{ transform: "scale(0.95)", mb: -1 }}
            onClick={(e) => {
              // Insulate the row action buttons from the row onClick, consistently
              // with the delete button (SN-19111). Harmless today (openModal is
              // idempotent with handleRowClick) but future-proof.
              e.stopPropagation();
              openModal();
            }}
          >
            <PictureInPictureIcon />
          </IconButton>
          <IconButton
            sx={{ transform: "scale(0.95)" }}
            onClick={(e) => {
              // Stop the click bubbling to the row onClick (which opens View Task) —
              // otherwise View opens over the delete confirm. SN-19111.
              e.stopPropagation();
              handleDelete(task);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
